// Static replay data for the Storm Explorer (precomputed by
// scripts/precompute/build_replays.py). The index is small; each per-storm
// replay (~60-110 KB) is lazy-loaded on selection.

export interface ReplayFrame {
  h: number;
  dt: string | null;
  lat: number;
  lon: number;
  vmax: number;
  pmin: number;
  vf: number;
  r34: number[] | null;
  r50: number[] | null;
  r64: number[] | null;
  idx: Record<string, number>; // CDI/IKE/FSTI/SGM/IFI normalized 0-10
  labels: Record<string, string>;
  overall: [number, string, string]; // [score, label, color]
  foot: Footprints;
}

export interface Footprints {
  mb: number | null; // motion bearing (deg)
  torn?: { tier: number; r34r: number }; // tornado right-front sector
  flood?: { tier: number; radii: number[] }; // IFI terrain-enhanced [NE,SE,SW,NW]
  // surge: [pointIndex, tier] pairs referencing the dense coastline (coastlineApi).
  // Tier 0 Minimal · 1 Impactful · 2 High · 3 Catastrophic.
  surge?: [number, number][];
}

export interface ReplayDoc {
  storm_id: string;
  name: string;
  year: number;
  total_hours: number;
  peak_hour_idx: number;
  frames: ReplayFrame[];
}

export interface ReplayIndexEntry {
  storm_id: string;
  name: string;
  display_name: string;
  year: number;
  category: string;
  overall_score: number;
  peak_hour_idx: number;
  total_hours: number;
}

export async function loadReplayIndex(): Promise<ReplayIndexEntry[]> {
  const res = await fetch("/data/replay_index.json");
  if (!res.ok) throw new Error(`replay_index → HTTP ${res.status}`);
  return res.json();
}

export async function loadReplay(stormId: string): Promise<ReplayDoc> {
  const res = await fetch(`/data/replays/${stormId}.json`);
  if (!res.ok) throw new Error(`replay ${stormId} → HTTP ${res.status}`);
  return res.json();
}

// Narrative cards sidecar — the guided "storm story" beats the Python engine
// computes for every replay (pre-authored + auto RI/stall/landfall/peak),
// serialized by scripts/precompute/build_narratives.py. Loaded once, lazily.
export interface NarrativeCard {
  h: number; // hour index into the replay frames
  t: string; // text
  y: string; // card type: pre_authored | ri_alert | stall | landfall | peak | fsti_spike | weakening
  a: string; // accent index (CDI/IKE/FSTI/IFI/SGM)
  i: string; // emoji icon
}
let _narrativesPromise: Promise<Record<string, NarrativeCard[]>> | null = null;
export function loadNarrativeCards(): Promise<Record<string, NarrativeCard[]>> {
  if (!_narrativesPromise) {
    _narrativesPromise = fetch("/data/narrative_cards.json")
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return _narrativesPromise;
}

// ── Dense coastline (built by build_coastline.py) — geometry for the surge
// ribbon + per-point region names for hover. Loaded once and cached. ──
export interface CoastlineDense {
  points: [number, number][]; // [lat, lon]
  arcs: [number, number][]; // [start, end) index ranges (ribbon must not cross these)
  faces: number[]; // seaward normal per point (deg)
  names: string[]; // nearest region name per point
}

let _coastlinePromise: Promise<CoastlineDense> | null = null;

export function loadCoastline(): Promise<CoastlineDense> {
  if (!_coastlinePromise) {
    _coastlinePromise = fetch("/data/coastline_dense.json").then((r) => {
      if (!r.ok) throw new Error(`coastline → HTTP ${r.status}`);
      return r.json();
    });
  }
  return _coastlinePromise;
}
